<template>
  <fieldset class="dpr-filters-fieldset">
    <legend class="dpr-filters-legend">Search filters</legend>
    <v-layout row wrap align-center justify-end>
    <v-flex xs12 md4 pa-2>
      <ParkAutocomplete
        v-model="parkCode"
        label="Athletic Field"
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Athletic Field filter">
            Use the <strong>Athletic Field</strong> feature to search Athletic Fields by park. 
            You can type a park in to the search bar or use the drop-down arrow to 
            search alphabetically.
          </Tooltip>
        </template>
      </ParkAutocomplete>
    </v-flex>
    <v-flex sm12 md2 pa-2>
      <v-combobox
        v-model="purpose"
        label="Purpose"
        clearable
        :items="['Game', 'Practice']"
        @change="$emit('change')"
        @click:clear="delayedChange"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Purpose filter">
            Use the <strong>Purpose</strong> feature to search by field use type. 
            “Practices” or “Games” are the most common use.
          </Tooltip>
        </template>
      </v-combobox>
    </v-flex>
    <v-flex xs12 sm6 md2 pa-2>
      <v-text-field
        v-model="reservee"
        label="Reservee"
        clearable
        @change="$emit('change')"
        @click:clear="delayedChange"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Reservee filter">
            Use the <strong>Reservee</strong> feature to search by field user group.
          </Tooltip>
        </template>
      </v-text-field>
    </v-flex>
    <v-flex xs12 sm6 md2 order-md6 pa-2>
      <v-select
        v-model="type"
        :items="typeOptions"
        label="Type"
        clearable
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Field type filter">
            Use the <strong>Type</strong>, <strong>Lighted</strong>, and 
            <strong>Turf type</strong> features to search for diamond fields, rectangular 
            fields, lighted field, non-lighted fields, synthetic, and natural grass fields.
          </Tooltip>
        </template>
      </v-select>
    </v-flex>
    <v-flex xs12 sm6 md2 order-md7 pa-2>
      <v-select
        v-model="lights"
        :items="lightOptions"
        label="Lighted"
        clearable
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Lighted filter">
            Use the <strong>Type</strong>, <strong>Lighted</strong>, and 
            <strong>Turf type</strong> features to search for diamond fields, rectangular 
            fields, lighted field, non-lighted fields, synthetic, and natural grass fields.
          </Tooltip>
        </template>
      </v-select>
    </v-flex>
    <v-flex xs12 sm6 md2 order-md9 pa-2>
      <v-select
        v-model="turfType"
        :items="turfTypeOptions"
        label="Turf type"
        clearable
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Turf type filter">
            Use the <strong>Type</strong>, <strong>Lighted</strong>, and 
            <strong>Turf type</strong> features to search for diamond fields, rectangular 
            fields, lighted field, non-lighted fields, synthetic, and natural grass fields.
          </Tooltip>
        </template>
      </v-select>
    </v-flex>
    <v-flex xs12 sm6 md2 order-md4 pa-2>
      <DatePicker
        v-model="startDate"
        label="Start date"
        :min="$store.state.config.minDate"
        :max="endDate"
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: Start date">
            Use the <strong>Start date and End date</strong> feature to search 
            within a specific timeframe.
          </Tooltip>
        </template>
      </DatePicker>
    </v-flex>
    <v-flex xs12 sm6 md2 order-md5 pa2>
      <DatePicker
        v-model="endDate"
        label="End date"
        clearable
        :min="startDate"
        :max="$store.state.config.maxDate"
        @change="$emit('change')"
      >
        <template v-slot:append>
          <Tooltip help-label="Help: End date">
            Use the <strong>Start date and End date</strong> feature to search 
            within a specific timeframe.
          </Tooltip>
        </template>
      </DatePicker>
    </v-flex>
    <v-flex xs12 md6 order-md10 pa-2>
      <TimePicker
        v-model="timeRange"
        @change="$emit('change')"
      />
    </v-flex>
  </v-layout>
  </fieldset>
</template>

<script>
  import DatePicker from '@/components/DatePicker'
  import ParkAutocomplete from '@/components/ParkAutocomplete'
  import TimePicker from '@/components/TimePicker'
  import Tooltip from '@/components/Tooltip'

  export default {
    data: () => ({
      typeOptions: [ 'DIAMD', 'RECT' ],
      lightOptions: [ 'Yes', 'No' ],
      statusOptions: [ 'Open', 'Closed' ],
      turfTypeOptions: [ 'Grass', 'Synthetic' ]
    }),
    components: {
      DatePicker,
      ParkAutocomplete,
      TimePicker,
      Tooltip
    },
    computed: {
      parkCode: {
        get () { return this.$store.state.index.parkCode },
        set (value) { this.$store.commit('setIndex', { filter: 'parkCode', value })}
      },
      purpose: {
        get () { return this.$store.state.index.purpose },
        set (value) { this.$store.commit('setIndex', { filter: 'purpose', value })}
      },
      reservee: {
        get () { return this.$store.state.index.reservee },
        set (value) { this.$store.commit('setIndex', { filter: 'reservee', value })}
      },
      type: {
        get () { return this.$store.state.index.type },
        set (value) { this.$store.commit('setIndex', { filter: 'type', value })}
      },
      lights: {
        get () { return this.$store.state.index.lights },
        set (value) { this.$store.commit('setIndex', { filter: 'lights', value })}
      },
      status: {
        get () { return this.$store.state.index.status },
        set (value) { this.$store.commit('setIndex', { filter: 'status', value })}
      },
      turfType: {
        get () { return this.$store.state.index.turfType },
        set (value) { this.$store.commit('setIndex', { filter: 'turfType', value })}
      },
      startDate: {
        get () { return this.$store.state.index.startDate },
        set (value) { this.$store.commit('setIndex', { filter: 'startDate', value})}
      },
      endDate: {
        get () { return this.$store.state.index.endDate },
        set (value) { this.$store.commit('setIndex', { filter: 'endDate', value})}
      },
      timeRange: {
        get () { return this.$store.state.index.timeRange },
        set (value) { this.$store.commit('setIndex', { filter: 'timeRange', value })}
      }
    },
    methods: {
      delayedChange () {
        this.$nextTick(function() {
          this.$emit('change')
        })
      }
    }
  }
</script>

<style scoped>
.dpr-filters-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.dpr-filters-legend {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>